import { BikeConfig, DEFAULT_BIKE } from '../sim/config';
import { decodeInput, encodeInput, InputFrame, INPUT_STRIDE_V2 } from '../sim/input';
import { LEVELS, LevelId } from '../sim/levels';
import { FrameState } from './frame';
import { GameWorld } from './world';

/** Numbers per step: v1 had five (throttle, brake, steer, body, standing); v2 adds the split brakes and the foot. */
export const INPUT_STRIDE = INPUT_STRIDE_V2;
export const strideOf = (v: 1 | 2): number => (v === 1 ? 5 : INPUT_STRIDE_V2);

/** A run as data: where it started, which physics it ran on, and every input. */
export interface Replay {
  v: 1 | 2;
  levelId: LevelId;
  start: string;
  configHash: string;
  dt: number;
  inputs: number[];
}

/** FNV-1a (32 bit) over the config with keys sorted, plus the step size. */
export function configHash(cfg: BikeConfig, dt: number): string {
  const text = JSON.stringify({ cfg, dt }, (_k, v: unknown) =>
    (v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
      : v));
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** Steps a fresh world and logs every input. Record from the first step: the world must not have been stepped. */
export class ReplayRecorder {
  private readonly inputs: number[] = [];

  constructor(readonly world: GameWorld) {
    if (world.steps !== 0) throw new Error('record from a fresh world');
  }

  step(input: InputFrame): FrameState {
    encodeInput(input, this.inputs);
    return this.world.step(input);
  }

  replay(): Replay {
    const w = this.world;
    return { v: 2, levelId: w.levelId, start: w.startName, configHash: configHash(w.cfg, w.dt), dt: w.dt, inputs: [...this.inputs] };
  }
}

/** JSON keeps doubles exact (shortest round-trip form), so the text form replays bit-identically. */
export const encodeReplay = (r: Replay): string => JSON.stringify(r);

export function decodeReplay(text: string): Replay {
  const r = JSON.parse(text) as Partial<Replay>;
  const ok = r && (r.v === 1 || r.v === 2) && typeof r.levelId === 'string' && r.levelId in LEVELS && typeof r.start === 'string'
    && typeof r.configHash === 'string' && typeof r.dt === 'number' && r.dt > 0
    && Array.isArray(r.inputs) && r.inputs.length % strideOf(r.v) === 0 && r.inputs.every((x) => typeof x === 'number' && Number.isFinite(x));
  if (!ok) throw new Error('malformed replay');
  return r as Replay;
}

/** Re-runs a replay in a fresh world; `onFrame` sees every frame (drills, coach, renderer). */
export function playReplay(r: Replay, cfg: BikeConfig = DEFAULT_BIKE, onFrame?: (f: FrameState) => void): GameWorld {
  if (configHash(cfg, r.dt) !== r.configHash) throw new Error('replay was recorded with a different bike config');
  const world = new GameWorld(r.levelId, r.start, cfg, r.dt);
  const x = r.inputs;
  const stride = strideOf(r.v);
  for (let i = 0; i < x.length; i += stride) {
    const f = world.step(decodeInput(x, i, stride));
    onFrame?.(f);
  }
  return world;
}
